import { useSearchParams } from 'react-router-dom';
import { get } from '../api';
import { currentLocale } from '../dates';
import { useLoad } from '../hooks';

interface Entry {
  id: number;
  object_type: string;
  object_id: number;
  action: string;
  message: string | null;
  logged_at: string;
  user_name: string | null;
  user_email: string | null;
}

const PAGE = 50;
const TYPES = ['', 'contact', 'assignment', 'visit', 'invoice', 'contractor_invoice'];

const stamp = (iso: string) =>
  new Date(iso).toLocaleString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Who changed what and when, newest first. */
export function Activity() {
  const [params, setParams] = useSearchParams();
  const type = params.get('type') ?? '';
  const offset = Number(params.get('offset') ?? 0) || 0;
  /** Change the filter or page but keep anything else in the address, such as the Settings tab. */
  const go = (next: Record<string, string>) => {
    const p = new URLSearchParams(params);
    p.delete('type');
    p.delete('offset');
    for (const [k, v] of Object.entries(next)) p.set(k, v);
    setParams(p);
  };
  const log = useLoad(() => {
    const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (type) qs.set('object_type', type);
    return get<{ items: Entry[] }>(`/api/activity?${qs}`);
  }, [type, offset]);
  const items = log.data?.items ?? [];

  return (
    <>
      <h2>Activity</h2>
      <div className="filters">
        <label>
          Show
          <select value={type} onChange={(e) => go(e.target.value ? { type: e.target.value } : {})}>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t ? t.replace('_', ' ') : 'Everything'}
              </option>
            ))}
          </select>
        </label>
      </div>
      {log.error && (
        <p role="alert" className="error">
          {log.error}
        </p>
      )}
      {log.loading && <p>Loading…</p>}
      {!log.loading && !log.error && items.length === 0 && (
        <p className="empty">Nothing recorded yet.</p>
      )}
      {items.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>What</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id}>
                <td>{stamp(e.logged_at)}</td>
                <td>{e.user_name || e.user_email || 'System'}</td>
                <td>
                  {e.object_type.replace('_', ' ')} #{e.object_id} {e.action.replace('_', ' ')}
                </td>
                <td>{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* Only when there is another page to go to. */}
      {(offset > 0 || items.length >= PAGE) && (
        <div className="pager">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => {
              const o = Math.max(0, offset - PAGE);
              go({ ...(type ? { type } : {}), ...(o ? { offset: String(o) } : {}) });
            }}
          >
            Newer
          </button>
          <button
            type="button"
            disabled={items.length < PAGE}
            onClick={() => go({ ...(type ? { type } : {}), offset: String(offset + PAGE) })}
          >
            Older
          </button>
        </div>
      )}
    </>
  );
}
